
import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Shield, Bike, Trash2, Mail, CheckCircle2, AlertCircle, Loader2, Lock, Edit2, X, Save } from 'lucide-react';
import { UserProfile, Driver } from '../../types';
import { supabase } from '../../services/supabase';
import { toast } from 'react-hot-toast';

interface Props {
  storeId: string;
  drivers: Driver[];
}

export const SettingsTabTeam: React.FC<Props> = ({ storeId, drivers }) => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    role: 'dispatcher' as 'dispatcher' | 'driver',
    associatedDriverId: ''
  });

  const [editData, setEditData] = useState<Partial<UserProfile>>({});
  const activeStoreId = storeId;

  const handleResponseError = (response: Response, error?: any): string => {
    if (response.status === 401) return 'Sessão expirada. Faça login novamente.';
    if (response.status === 403) return 'Acesso negado. Apenas administradores podem criar contas.';
    if (response.status === 400) return error?.message || 'Dados inválidos.';
    if (response.status >= 500) return 'Erro no servidor. Tente novamente mais tarde.';
    return error?.message || 'Falha ao criar conta';
  };

  useEffect(() => {
    if (!activeStoreId) { setIsLoadingList(false); return; }

    const loadUsers = async () => {
      const { data, error } = await supabase
        .from('sal_user_identities')
        .select('auth_user_id,email,role,store_id,associated_driver_id')
        .eq('store_id', activeStoreId);

      if (error) {
        console.error("Erro listagem:", error);
        setIsLoadingList(false);
        return;
      }

      const mapped = (data || []).map((row: any) => ({
        uid: row.auth_user_id || `${row.email}`,
        email: row.email,
        role: row.role,
        storeId: row.store_id,
        associatedDriverId: row.associated_driver_id || undefined
      } as UserProfile));

      setUsers(mapped);
      setIsLoadingList(false);
    };

    loadUsers();

    const channel = supabase
      .channel(`sal-users-${activeStoreId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sal_user_identities', filter: `store_id=eq.${activeStoreId}` },
        () => loadUsers()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeStoreId]);

  const reloadUsers = async () => {
    if (!activeStoreId) return;
    const { data, error } = await supabase
      .from('sal_user_identities')
      .select('auth_user_id,email,role,store_id,associated_driver_id')
      .eq('store_id', activeStoreId);

    if (!error && data) {
      const mapped = (data || []).map((row: any) => ({
        uid: row.auth_user_id || `${row.email}`,
        email: row.email,
        role: row.role,
        storeId: row.store_id,
        associatedDriverId: row.associated_driver_id || undefined
      } as UserProfile));
      setUsers(mapped);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserData.email || !newUserData.password) {
      toast.error("Preencha e-mail e senha.");
      return;
    }
    if (!activeStoreId) {
      toast.error("Erro crítico: Loja não identificada. Recarregue a página.");
      return;
    }

    setIsCreating(true);

    try {
      const { data, error } = await supabase.functions.invoke('sal-team-admin', {
        body: {
          action: 'create',
          email: newUserData.email.trim().toLowerCase(),
          password: newUserData.password,
          role: newUserData.role,
          associatedDriverId: newUserData.associatedDriverId || null
        }
      });

      if (error) throw new Error(error.message || 'Falha ao criar conta');
      if (data?.error) throw new Error(data.error);

      toast.success("Colaborador criado!");
      setIsAdding(false);
      setNewUserData({ email: '', password: '', role: 'dispatcher', associatedDriverId: '' });
      await reloadUsers();

    } catch (error: any) {
      console.error("[SAL-DEBUG] Erro criação:", error);
      toast.error(handleResponseError(new Response(JSON.stringify({ status: 500 }), { status: 500 }), error));
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUserId) return;
    setIsUpdating(true);
    try {
      const { data, error } = await supabase.functions.invoke('sal-team-admin', {
        body: {
          action: 'update',
          userId: editingUserId,
          role: editData.role,
          associatedDriverId: editData.role === 'driver' ? editData.associatedDriverId : null
        }
      });

      if (error) throw new Error(error.message || 'Erro ao atualizar');
      if (data?.error) throw new Error(data.error);

      toast.success("Perfil atualizado!");
      setEditingUserId(null);
      await reloadUsers();
    } catch (error: any) {
      toast.error(handleResponseError(new Response(JSON.stringify({ status: 500 }), { status: 500 }), error));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteUser = async (user: UserProfile) => {
    if (user.role === 'admin') return;
    if (!confirm(`Remover acesso de ${user.email}?`)) return;
    try {
      const { data, error } = await supabase.functions.invoke('sal-team-admin', {
        body: { action: 'delete', userId: user.uid }
      });

      if (error) throw new Error(error.message || 'Erro ao remover');
      if (data?.error) throw new Error(data.error);

      toast.success("Acesso removido.");
      await reloadUsers();
    } catch (error: any) {
      toast.error(handleResponseError(new Response(JSON.stringify({ status: 500 }), { status: 500 }), error));
    }
  };

  const startEdit = (user: UserProfile) => {
    setEditingUserId(user.uid);
    setEditData({ role: user.role, associatedDriverId: user.associatedDriverId });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-text-primary font-black uppercase tracking-widest text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-sal-cyber-teal" /> Gestão de Equipe
          </h3>
          <p className="text-xs text-text-muted font-bold uppercase tracking-wider mt-1">
            ID da Loja Ativo: <span className="font-mono text-sal-cyber-teal/60">{activeStoreId}</span>
          </p>
        </div>
        <button
          onClick={() => { setIsAdding(!isAdding); setEditingUserId(null); }}
          className="bg-sal-cyber-teal/10 hover:bg-sal-cyber-teal/20 text-sal-cyber-teal px-4 py-2 rounded-xl border border-sal-cyber-teal/20 text-xs font-black uppercase tracking-widest transition-all"
        >
          {isAdding ? 'Cancelar' : 'Cadastrar Novo'}
        </button>
      </div>

      {isAdding && (
        <div className="bg-card-bg border border-border-dim rounded-3xl p-6 space-y-4 animate-in slide-in-from-top-4 duration-300 shadow-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-black text-text-muted uppercase tracking-widest ml-1">E-mail de Login</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input type="email" value={newUserData.email} onChange={e => setNewUserData({ ...newUserData, email: e.target.value })} placeholder="email@equipe.com" className="w-full bg-input-bg border border-input-border rounded-2xl py-3 pl-12 pr-4 text-base text-text-primary outline-none focus:border-sal-cyber-teal/50 transition-all" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-black text-text-muted uppercase tracking-widest ml-1">Senha Inicial</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input type="password" value={newUserData.password} onChange={e => setNewUserData({ ...newUserData, password: e.target.value })} placeholder="Mínimo 6 dígitos" className="w-full bg-input-bg border border-input-border rounded-2xl py-3 pl-12 pr-4 text-base text-text-primary outline-none focus:border-sal-cyber-teal/50 transition-all" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-black text-text-muted uppercase tracking-widest ml-1">Cargo / Permissão</label>
              <select value={newUserData.role} onChange={e => setNewUserData({ ...newUserData, role: e.target.value as any })} className="w-full bg-input-bg border border-input-border rounded-2xl py-3 px-4 text-base text-text-primary outline-none focus:border-sal-cyber-teal/50 transition-all appearance-none cursor-pointer">
                <option value="dispatcher">Despachante (Cockpit)</option>
                <option value="driver">Motoboy (App Piloto)</option>
              </select>
            </div>
            {newUserData.role === 'driver' && (
              <div className="space-y-2 animate-in fade-in duration-300">
                <label className="text-xs font-black text-status-info uppercase tracking-widest ml-1">Vincular a qual Piloto?</label>
                <select value={newUserData.associatedDriverId} onChange={e => setNewUserData({ ...newUserData, associatedDriverId: e.target.value })} className="w-full bg-status-info/10 border border-status-info/20 rounded-2xl py-3 px-4 text-base text-text-primary outline-none appearance-none cursor-pointer">
                  <option value="">Selecione...</option>
                  {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <button onClick={handleCreateUser} disabled={isCreating || !activeStoreId} className="w-full bg-sal-cyber-teal text-badge-text-inv py-4 rounded-2xl font-black uppercase tracking-[0.2em] text-xs shadow-lg flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-50 transition-all">
            {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Criar e Autorizar Acesso
          </button>
        </div>
      )}

      <div className="bg-bg-main/30 rounded-[2rem] border border-border-dim overflow-hidden shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-border-dim bg-bg-main/50">
              <th className="px-6 py-4 text-xs font-black text-text-muted uppercase tracking-widest">Colaborador</th>
              <th className="px-6 py-4 text-xs font-black text-text-muted uppercase tracking-widest">Nível de Acesso</th>
              <th className="px-6 py-4 text-xs font-black text-text-muted uppercase tracking-widest text-right">Ação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-dim">
            {isLoadingList ? (
              <tr><td colSpan={3} className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-sal-cyber-teal mx-auto" /></td></tr>
            ) : users.map((user, idx) => (
              <React.Fragment key={user.uid}>
                <tr className={`transition-colors group ${editingUserId === user.uid ? 'bg-sal-cyber-teal/5' : 'hover:bg-bg-main/20'}`}>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-text-primary font-bold text-base">{user.email}</span>
                      <span className="text-xs text-text-muted font-mono">UID: {user.uid}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest ${user.role === 'admin' ? 'bg-sal-cyber-teal/10 text-sal-cyber-teal' : user.role === 'driver' ? 'bg-status-info/10 text-status-info' : 'bg-status-pending/10 text-status-pending'}`}>
                      {user.role === 'admin' ? <Shield className="w-3 h-3" /> : user.role === 'driver' ? <Bike className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                      {user.role === 'admin' ? 'Administrador' : user.role === 'driver' ? 'Motoboy' : 'Despachante'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {user.role !== 'admin' && (
                        <>
                          <button onClick={() => editingUserId === user.uid ? setEditingUserId(null) : startEdit(user)} className={`p-2 rounded-lg transition-all ${editingUserId === user.uid ? 'text-sal-cyber-teal bg-sal-cyber-teal/10' : 'text-text-muted hover:text-sal-cyber-teal hover:bg-sal-cyber-teal/10'}`}>
                            {editingUserId === user.uid ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleDeleteUser(user)} className="p-2 text-text-muted hover:text-status-critical hover:bg-status-critical/10 rounded-lg transition-all"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {editingUserId === user.uid && (
                  <tr className="bg-sal-cyber-teal/5 border-b border-border-dim shadow-inner">
                    <td colSpan={3} className="px-6 py-4">
                      <div className="flex flex-wrap items-end gap-4 animate-in slide-in-from-left-2 duration-200">
                        <div className="space-y-1.5">
                          <label className="text-xs font-black text-sal-cyber-teal uppercase tracking-widest ml-1">Alterar Cargo</label>
                          <select value={editData.role} onChange={e => setEditData({ ...editData, role: e.target.value as any })} className="bg-input-bg border border-border-dim rounded-xl py-2 px-3 text-sm text-text-primary outline-none focus:border-sal-cyber-teal/50 transition-all">
                            <option value="dispatcher">Despachante</option>
                            <option value="driver">Motoboy</option>
                          </select>
                        </div>
                        {editData.role === 'driver' && (
                          <div className="space-y-1.5 animate-in fade-in duration-300">
                            <label className="text-xs font-black text-status-info uppercase tracking-widest ml-1">Vínculo Frota</label>
                            <select value={editData.associatedDriverId || ''} onChange={e => setEditData({ ...editData, associatedDriverId: e.target.value })} className="bg-input-bg border border-border-dim rounded-xl py-2 px-3 text-sm text-text-primary outline-none focus:border-status-info/50 transition-all">
                              <option value="">Nenhum</option>
                              {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                          </div>
                        )}
                        <button onClick={handleUpdateUser} disabled={isUpdating} className="bg-sal-cyber-teal text-badge-text-inv px-4 py-2 rounded-xl font-black uppercase text-xs flex items-center gap-2 hover:brightness-110 disabled:opacity-50 h-[38px] transition-all">
                          {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          Salvar Alterações
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
